import {Injectable, inject} from '@angular/core';
import {Observable, of, Subscription, forkJoin} from "rxjs";
import {filter, map} from "rxjs";
import {TYPE} from "../types/types";
import {ApiService} from "./api.service";
import {HttpEventType, HttpResponse} from "@angular/common/http";
import {FileStorageResource} from '../types/generated/fileStorageResource';
import {FileStorageType} from '../types/generated/fileStorageType';

@Injectable({
  providedIn: 'root'
})
export class DocumentService {
  private apiService: ApiService = inject(ApiService);
  private readonly documentOpenPendingRoute: string = '/document-open-pending';
  private readonly documentOpenTimeoutMs: number = 15000;
  private readonly documentOpenPopupClosePollMs: number = 500;

  public allowedImageTypes: string[] = ['gif', 'jpg', 'jpeg', 'png'];
  public allowedVideoTypes: string[] = ['mov', 'mp4'];
  public allowedAudioTypes: string[] = ['mp3'];
  public allowedMediaTypes: string[] = [
    ...this.allowedImageTypes,
    ...this.allowedVideoTypes, 
    ...this.allowedAudioTypes
  ];
  public documentsGalleryDialog: boolean = false;
  public documentsGalleryHideReactions: boolean = false;
  public documentsGalleryForwardDialog: boolean = false;
  public documentsGalleryFiles: FileStorageResource[] = [];
  public documentsGalleryPosition: number = 0;
  public documentsGalleryMessageId: number = 0;
  public documentsGalleryFileToMessageMap: Map<string, number> = new Map<string, number>();
  public documentsGalleryShowChatOptions: boolean = false;
  private fileSubscription: Subscription[] = [];

  public uploadFiles(documents: FileList, directory?: string, isPublic?: boolean): Observable<FileStorageResource[]> {
    if (documents.length === 0) {
      return of([]);
    }

    const uploadObservables = Array.from(documents).map((document) =>
      this.apiService.storageUpload(document, directory, isPublic).pipe(
        filter((event): event is HttpResponse<FileStorageResource> => event.type === HttpEventType.Response),
        map((response) => response.body!)
      )
    );

    return forkJoin(uploadObservables);
  }

  public downloadDocument(document: FileStorageResource): void {
    if (!document.path) {
      return;
    }
    const fileName = document.filename || 'download';
    this.apiService.downloadByPath(document.path).subscribe({
      next: (blob: Blob) => this.triggerBlobDownload(blob, fileName),
      error: (error: unknown) => console.error('Download failed:', error)
    });
  }

  public getDocumentOpenUrl(document: FileStorageResource): Observable<string> {
    if (!document.uuid) {
      return of('');
    }

    return this.apiService.getStorageOpenUrl(document.uuid).pipe(
      map(response => response.url)
    );
  }

  public openDocument(document: FileStorageResource): void {
    if (!document.uuid) {
      return;
    }

    const target = window.open(this.documentOpenPendingRoute, '_blank');
    if (!target) {
      this.getDocumentOpenUrl(document).subscribe({
        next: (url: string) => {
          if (this.isSafeDocumentOpenUrl(url)) {
            this.navigateCurrentWindow(url);
          }
        },
        error: (error: unknown) => console.error('Open document failed:', error)
      });
      return;
    }

    let signedUrl: string | null = null;
    let pendingPageReady: boolean = false;
    let completed: boolean = false;
    let signedUrlSubscription: Subscription | null = null;

    const cleanup = (): void => {
      window.removeEventListener('message', handleReadyMessage);
      window.clearTimeout(timeoutId);
      window.clearInterval(closePollId);
    };

    const closeTarget = (): void => {
      try {
        target.close();
      } catch {
        // Nothing else to do if the browser refuses to close the bridge tab.
      }
    };

    const fail = (error?: unknown): void => {
      if (completed) {
        return;
      }

      completed = true;
      cleanup();
      closeTarget();

      if (error) {
        console.error('Open document failed:', error);
      }
    };

    const postSignedUrlIfReady = (): void => {
      if (completed || !pendingPageReady || !signedUrl) {
        return;
      }

      try {
        target.postMessage(
          {
            type: 'open-document',
            url: signedUrl
          },
          window.location.origin
        );
        completed = true;
        cleanup();
      } catch (error: unknown) {
        fail(error);
      }
    };

    const handleReadyMessage = (event: MessageEvent): void => {
      if (
        event.origin !== window.location.origin ||
        event.source !== target ||
        !this.isDocumentOpenReadyMessage(event.data)
      ) {
        return;
      }

      pendingPageReady = true;
      postSignedUrlIfReady();
    };

    window.addEventListener('message', handleReadyMessage);
    const timeoutId = window.setTimeout(() => {
      fail(new Error('Document open timed out.'));
    }, this.documentOpenTimeoutMs);
    const closePollId = window.setInterval(() => {
      if (target.closed) {
        completed = true;
        cleanup();
        signedUrlSubscription?.unsubscribe();
      }
    }, this.documentOpenPopupClosePollMs);

    try {
      signedUrlSubscription = this.getDocumentOpenUrl(document).subscribe({
        next: (url: string) => {
          if (!this.isSafeDocumentOpenUrl(url)) {
            fail();
            return;
          }

          signedUrl = url;
          postSignedUrlIfReady();
        },
        error: (error: unknown) => {
          fail(error);
        }
      });
    } catch (error: unknown) {
      fail(error);
    }
  }

  public downloadData(data: BlobPart, fileName: string, mimeType = 'application/octet-stream'): void {
    this.triggerBlobDownload(new Blob([data], {type: mimeType}), fileName);
  }

  public downloadFromUrl(url: string, fileName: string): void {
    if (!url) {
      return;
    }
    if (url.startsWith('blob:')) {
      this.triggerUrlDownload(url, fileName);
      return;
    }
    this.apiService.downloadByUrl(url).subscribe({
      next: (blob: Blob) => this.triggerBlobDownload(blob, fileName),
      error: (error: unknown) => console.error('Download failed:', error)
    });
  }

  public mergeDocuments(...documentsArrays: FileStorageResource[][]): FileStorageResource[] {
    return documentsArrays.flat();
  }

  public getDocumentByType(documents: FileStorageResource[], type: FileStorageType): FileStorageResource[] {
    return documents.filter((document) => document.type === type);
  }

  public getDocumentClass(document: FileStorageResource): 'file-default' | 'file-excel' | 'file-word' | 'file-pdf' | 'file-zip' {
    const extension = this.getDocumentExtension(document);
    if (extension === 'pdf') {
      return 'file-pdf';
    } else if (extension === 'xls' || extension === 'xlsx') {
      return 'file-excel';
    } else if (extension === 'doc' || extension === 'docx') {
      return 'file-word';
    } else if (extension === 'zip') {
      return 'file-zip';
    } else {
      return 'file-default';
    }
  }

  public getLocalDocumentClass(document: TYPE.LocalDocument): 'file-default' | 'file-excel' | 'file-word' | 'file-pdf' | 'file-zip' {
    const extension = document.filename.split('.').pop()!.toLowerCase();
    if (extension === 'pdf') {
      return 'file-pdf';
    } else if (extension === 'xls' || extension === 'xlsx') {
      return 'file-excel';
    } else if (extension === 'doc' || extension === 'docx') {
      return 'file-word';
    } else if (extension === 'zip') {
      return 'file-zip';
    } else {
      return 'file-default';
    }

  }

  public getDocumentExtension(document: FileStorageResource): string {
    // if (document.localFile) {
    //   return document.localFile.name.split('.').pop()!;
    // }
    return document.filename!.split('.').pop()!;
  }

  public getLocalDocumentExtension(document: TYPE.LocalDocument): string {
    return document.filename.split('.').pop()!.toLowerCase();
  }

  public showGallery(
    media: FileStorageResource[],
    document: FileStorageResource,
    hideReactions: boolean = true,
    messageId?: number,
    fileToMessageMap?: Map<string, number>,
    showChatOptions: boolean = false
  ): void {
    this.documentsGalleryDialog = true;
    this.documentsGalleryFiles = media;
    this.documentsGalleryPosition = media.indexOf(document) !== -1 ? media.indexOf(document) : media.findIndex((file) => file.uuid === document.uuid);
    this.documentsGalleryHideReactions = hideReactions;
    this.documentsGalleryMessageId = messageId ?? 0;
    this.documentsGalleryFileToMessageMap = fileToMessageMap ?? new Map();
    this.documentsGalleryShowChatOptions = showChatOptions;
  }

  public cancelUploads(): void {
    this.fileSubscription.forEach((subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });
    this.fileSubscription = [];
  }

  public resetState(): void {
    this.cancelUploads();
    this.documentsGalleryDialog = false;
    this.documentsGalleryHideReactions = false;
    this.documentsGalleryForwardDialog = false;
    this.documentsGalleryFiles = [];
    this.documentsGalleryPosition = 0;
    this.documentsGalleryMessageId = 0;
    this.documentsGalleryFileToMessageMap = new Map<string, number>();
    this.documentsGalleryShowChatOptions = false;
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    try {
      this.triggerUrlDownload(blobUrl, fileName);
    } finally {
      window.URL.revokeObjectURL(blobUrl);
    }
  }

  private triggerUrlDownload(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    try {
      link.click();
    } finally {
      document.body.removeChild(link);
    }
  }

  private navigateCurrentWindow(url: string): void {
    window.location.href = url;
  }

  private isSafeDocumentOpenUrl(url: string): boolean {
    if (!url) {
      return false;
    }

    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.protocol === 'https:') {
        return true;
      }

      return parsedUrl.protocol === 'http:' && this.isLocalhost(parsedUrl.hostname);
    } catch {
      return false;
    }
  }

  private isLocalhost(hostname: string): boolean {
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]' || hostname === '::1';
  }

  private isDocumentOpenReadyMessage(data: unknown): data is {type: 'document-open-ready'} {
    if (!data || typeof data !== 'object') {
      return false;
    }

    return (data as {type?: unknown}).type === 'document-open-ready';
  }
}
